import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { AdminTransactionsResult, AdminTransaction, RevenueStats } from '../../api/admin';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs } from '../../components/admin';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StatusChip({ status }: { status: AdminTransaction['status'] }) {
  switch (status) {
    case 'succeeded':
      return <Chip label="Completed" color="success" size="small" icon={<CheckCircleIcon />} />;
    case 'failed':
      return <Chip label="Failed" color="error" size="small" icon={<ErrorIcon />} />;
    case 'pending':
    default:
      return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
  }
}

function ModeChip({ mode }: { mode: 'test' | 'live' }) {
  return (
    <Chip
      label={mode === 'live' ? 'Live' : 'Test'}
      color={mode === 'live' ? 'success' : 'default'}
      size="small"
      variant="outlined"
    />
  );
}

function RevenueStatsCards({ stats }: { stats: RevenueStats }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MoneyIcon color="success" />
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
          </Box>
          <Typography variant="h5" color="success.main">
            {formatCurrency(stats.totalRevenue)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Live: {formatCurrency(stats.liveRevenue)} | Test: {formatCurrency(stats.testRevenue)}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="body2" color="text.secondary">
              Total Transactions
            </Typography>
          </Box>
          <Typography variant="h5">
            {stats.totalTransactions}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="body2" color="text.secondary">
              Successful
            </Typography>
          </Box>
          <Typography variant="h5" color="success.main">
            {stats.successfulPayments}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="body2" color="text.secondary">
              Failed / Pending
            </Typography>
          </Box>
          <Typography variant="h5">
            {stats.failedPayments} / {stats.pendingPayments}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

export function TransactionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Transactions');

  const [result, setResult] = useState<AdminTransactionsResult | null>(null);
  const [stats, setStats] = useState<RevenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || ''
  );
  const [modeFilter, setModeFilter] = useState<string>(
    searchParams.get('mode') || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const [transactionsData, statsData] = await Promise.all([
        adminApi.listTransactions({
          status: statusFilter || undefined,
          mode: modeFilter || undefined,
          page: page + 1,
          pageSize,
        }),
        adminApi.getRevenueStats(),
      ]);
      setResult(transactionsData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, modeFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchTransactions();
  }, [currentUser, navigate, fetchTransactions]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleModeChange = (value: string) => {
    setModeFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('mode', value);
    } else {
      params.delete('mode');
    }
    setSearchParams(params);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Transactions' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Stripe Transactions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Revenue Stats */}
      {stats && <RevenueStatsCards stats={stats} />}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="succeeded">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Mode</InputLabel>
              <Select
                value={modeFilter}
                label="Mode"
                onChange={(e) => handleModeChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="live">Live</MenuItem>
                <MenuItem value="test">Test</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Mode</TableCell>
                <TableCell align="center">Receipt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.transactions.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(tx.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tx.user?.displayName || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tx.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tx.product?.name || 'Unknown Product'}
                    </Typography>
                    {tx.product?.metadata?.boardCredits && (
                      <Typography variant="caption" color="text.secondary">
                        +{tx.product.metadata.boardCredits} credits
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {tx.cardLast4 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CardIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {tx.cardBrand} •••• {tx.cardLast4}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {tx.paymentMethodType || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(tx.amount)}
                    </Typography>
                    {tx.discountAmount > 0 && (
                      <Typography variant="caption" color="success.main">
                        -{formatCurrency(tx.discountAmount)} discount
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip status={tx.status} />
                    {tx.failureMessage && (
                      <Tooltip title={tx.failureMessage}>
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'block', mt: 0.5, cursor: 'help' }}
                        >
                          {tx.failureCode}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <ModeChip mode={tx.mode} />
                  </TableCell>
                  <TableCell align="center">
                    {tx.receiptUrl ? (
                      <IconButton
                        size="small"
                        href={tx.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View Receipt"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {result?.transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>
    </Box>
  );
}
